import * as React from "react";

// Document-level key handler shared across screens. Skips typing surfaces
// (input/textarea/contenteditable/select) so a search field can't be
// hijacked by a one-letter shortcut, and ignores modifier-key combos so
// browser hotkeys (Cmd-R, Ctrl-F) keep working.
//
// Pass `handlers` as a stable object — the effect re-binds whenever its
// identity changes. Wrap the map in useMemo at the call site if the
// handlers are derived from changing state.

type Handler = (e: KeyboardEvent) => void;

export function useKeyboardShortcuts(
  handlers: Record<string, Handler | undefined>,
  enabled: boolean = true,
): void {
  React.useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (target?.isContentEditable) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const handler = handlers[e.key];
      if (!handler) return;
      handler(e);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [handlers, enabled]);
}
